import { type Permission, canReadTaxNfe } from "@backchannel/tax-contracts";
import { useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Page } from "~/components/layout/page";
import { DeniedState } from "~/components/ui/states";
import { useT } from "~/i18n";
import { useSession } from "./session-context";

/** Stop restricted leaves before mounting their queries, including direct URLs. */
export function CommercialRouteBoundary({ children }: { children: ReactNode }) {
  const { effectiveRole, can, permissions } = useSession();
  const { pathname, searchStr } = useRouterState({ select: (state) => state.location });
  const t = useT();
  if (effectiveRole !== "TAX_COMMERCIAL") return children;
  const calculator = pathname === "/calculations" || pathname.startsWith("/calculations/");
  const personal =
    [
      "/account",
      "/settings",
      "/settings/preferences",
      "/settings/password",
      "/settings/passkeys",
      "/legal",
    ].includes(pathname) && new URLSearchParams(searchStr).get("workspace") !== "developers";
  const allowed =
    pathname === "/" ||
    personal ||
    ((pathname === "/commercial" ||
      pathname === "/lots" ||
      (calculator && pathname !== "/calculations/research")) &&
      can("READ:TAX_CALCULATION:ALL")) ||
    (pathname === "/invoices" && canReadTaxNfe(permissions));
  if (allowed) return children;
  const permission: Permission =
    pathname === "/overview"
      ? "READ:TAX_OVERVIEW:ALL"
      : pathname === "/invoices"
        ? "READ:TAX_NFE:ALL"
        : pathname === "/commercial" || calculator || pathname === "/lots"
          ? "READ:TAX_CALCULATION:ALL"
          : pathname.startsWith("/admin") || pathname === "/users"
            ? "ADMIN:TAX_PLATFORM:ALL"
            : "READ:TAX_RULE:ALL";
  return (
    <Page title={t("access.denied.title")}>
      <DeniedState permission={permission} />
    </Page>
  );
}
